import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Mascot from "@/components/Mascot";
import { ATT_STATUS, TYPE_LABEL } from "@/lib/constants";
import {
  Camera, Star, Award, Clock, Zap, FileText, Loader2, ArrowRight, CalendarCheck, Trophy, PartyPopper, X, Volume2, Medal,
} from "lucide-react";
import { Confetti, playCheerSound } from "@/components/Confetti";

const BADGE_ICONS = { Clock, Zap, FileText, Award, Medal };

function CheerBanner({ cheer, onDismiss }) {
  const [confetti, setConfetti] = useState(true);
  const [wiggle, setWiggle] = useState(true);
  if (!cheer) return null;
  const replay = () => { setConfetti(false); setWiggle(false); setTimeout(() => { setConfetti(true); setWiggle(true); }, 20); };
  return (
    <>
      {confetti && <Confetti onDone={() => setConfetti(false)} />}
      <div className="relative flex items-start gap-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl p-5 sm:p-6 text-white shadow-md animate-pop" data-testid="cheer-banner">
        <div className={`w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center shrink-0 ${wiggle ? "animate-wiggle" : ""}`}><PartyPopper className="w-6 h-6" /></div>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wide text-emerald-50/90 font-semibold">Pesan Semangat</p>
          <p className="font-display text-lg sm:text-xl font-bold mt-1 leading-snug" data-testid="cheer-message">{cheer.message}</p>
          <button onClick={() => { replay(); playCheerSound(); }} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold bg-white/20 hover:bg-white/30 rounded-full px-3 py-1.5 transition-colors" data-testid="cheer-replay">
            <Volume2 className="w-3.5 h-3.5" /> Rayakan lagi!
          </button>
        </div>
        <button onClick={onDismiss} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors" aria-label="Tutup" data-testid="cheer-dismiss"><X className="w-4 h-4" /></button>
      </div>
    </>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/murid").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const dismissCheer = async () => {
    const id = data?.encouragement?.id;
    setData((d) => ({ ...d, encouragement: null }));
    if (id) api.put(`/notifications/${id}/read`).catch(() => {});
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;

  const att = data.attendance_today;
  const attSt = att ? (ATT_STATUS[att.status] || ATT_STATUS.menunggu) : null;
  const earnedBadges = (data.badges || []);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <CheerBanner cheer={data.encouragement} onDismiss={dismissCheer} />
      {/* greeting */}
      <div className="flex items-center gap-4 bg-gradient-to-r from-amber-400 to-amber-500 rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="absolute -right-4 -bottom-6 opacity-95"><Mascot size={120} className="animate-float" /></div>
        <div className="relative z-10">
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Hai, {data.name}! 🌟</h1>
          <p className="mt-2 text-amber-50">Selamat belajar hari ini. Yuk cek tugas dan isi absensimu!</p>
          <div className="mt-4 inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 text-sm font-medium">
            <Star className="w-4 h-4" /> {data.points} Bintang Belajar
          </div>
        </div>
      </div>

      {/* attendance + quick */}
      <div className="grid md:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-2 text-slate-800 font-display font-semibold"><CalendarCheck className="w-5 h-5 text-teal-600" /> Absensi Hari Ini</div>
          {att ? (
            <div className="mt-4 flex items-center gap-3">
              <span className={`px-4 py-2 rounded-full border font-medium ${attSt.cls}`}>{attSt.label}</span>
              <span className="text-sm text-slate-400">Jam {att.time} WIB</span>
            </div>
          ) : (
            <button onClick={() => navigate("/murid/absensi")} className="mt-4 w-full flex items-center justify-center gap-2 h-14 rounded-2xl bg-teal-600 hover:bg-teal-700 text-white font-semibold text-lg transition-colors" data-testid="dashboard-absen-btn">
              <Camera className="w-6 h-6" /> Isi Absensi Sekarang
            </button>
          )}
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-2 text-slate-800 font-display font-semibold"><Trophy className="w-5 h-5 text-amber-500" /> Pencapaianku</div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="bg-amber-50 rounded-xl p-3 text-center">
              <p className="font-display text-2xl font-bold text-amber-600" data-testid="student-star-points-counter">{data.points}</p>
              <p className="text-xs text-amber-700">Bintang</p>
            </div>
            <div className="bg-indigo-50 rounded-xl p-3 text-center">
              <p className="font-display text-2xl font-bold text-indigo-600">{data.average_score ?? "—"}</p>
              <p className="text-xs text-indigo-700">Rata-rata Nilai</p>
            </div>
          </div>
        </div>
      </div>

      {/* badges */}
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
        <h3 className="font-display text-lg font-semibold text-slate-800 mb-4">Lencana Prestasi</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Object.entries(data.badge_defs).map(([id, def]) => {
            const Icon = BADGE_ICONS[def.icon] || Award;
            const earned = earnedBadges.includes(id);
            return (
              <div key={id} data-testid={`student-badge-item-${id}`} className={`rounded-2xl p-4 text-center border transition-all ${earned ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-100 opacity-50 grayscale"}`}>
                <div className={`w-12 h-12 rounded-full mx-auto flex items-center justify-center ${earned ? "bg-amber-400 text-white" : "bg-slate-200 text-slate-400"}`}><Icon className="w-6 h-6" /></div>
                <p className="text-xs font-semibold text-slate-700 mt-2">{def.name}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* active assignments */}
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold text-slate-800">Tugas Aktif</h3>
          <button onClick={() => navigate("/murid/tugas")} className="text-sm text-teal-600 font-medium flex items-center gap-1" data-testid="see-all-assignments">Lihat Semua <ArrowRight className="w-4 h-4" /></button>
        </div>
        {data.active_assignments.length === 0 ? (
          <p className="text-sm text-slate-400">Tidak ada tugas aktif. Kerja bagus! 🎉</p>
        ) : (
          <div className="space-y-2">
            {data.active_assignments.map((a) => (
              <button key={a.id} onClick={() => navigate(`/murid/tugas/${a.id}`)} data-testid={`dashboard-assignment-${a.id}`}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-slate-100 hover:border-teal-300 hover:bg-teal-50/40 transition-colors text-left">
                <div>
                  <p className="font-medium text-slate-800">{a.title}</p>
                  <p className="text-xs text-slate-500">{TYPE_LABEL[a.type]}{a.due_date ? ` · Tenggat ${a.due_date.slice(0, 10)}` : ""}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
